import React from 'react';
import { STATUS_OPTIONS } from '../constants';
import SharedSearchInput from '../../../shared/SharedSearchInput/SharedSearchInput';

/**
 * Toolbar component for ProjectList, containing search, filter, view toggle, and create button.
 * 
 * @param {Object} props
 * @param {string} props.searchTerm
 * @param {Function} props.setSearchTerm
 * @param {boolean} props.isFilterOpen
 * @param {Function} props.setIsFilterOpen
 * @param {Function} props.getFilterLabel
 * @param {Array} props.statusFilter
 * @param {Function} props.toggleStatusFilter
 * @param {Function} props.handleSelectAllFilters
 * @param {Function} props.handleSelectNoneFilters
 * @param {string} props.viewMode
 * @param {Function} props.setViewMode
 * @param {Object} props.globalPermissions
 * @param {Function} props.openCreateProject
 * @param {React.RefObject} props.filterRef
 */
export default function ProjectToolbar({
    searchTerm,
    setSearchTerm,
    isFilterOpen,
    setIsFilterOpen,
    getFilterLabel,
    statusFilter,
    toggleStatusFilter,
    handleSelectAllFilters,
    handleSelectNoneFilters,
    viewMode,
    setViewMode,
    globalPermissions,
    openCreateProject,
    filterRef,
    isTrashOpen,
    setIsTrashOpen,
    trashCount
}) {
    return (
        <div className="action-bar" style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center', 
            marginBottom: '25px', 
            background: '#fff', 
            padding: '12px', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            maxWidth: '1200px',
            margin: '0 auto',
            boxSizing: 'border-box'
        }}>
            <div style={{ flex: 1, position: 'relative' }}>
                <SharedSearchInput 
                    placeholder="Suche in Projekten..." 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    onClear={() => setSearchTerm('')}
                    style={{ height: '40px' }}
                />
            </div>
            
            <div className="filter-dropdown-container" ref={filterRef} style={{ height: '40px' }}>
                <button className="filter-dropdown-btn" onClick={() => setIsFilterOpen(!isFilterOpen)} style={{ height: '40px', display: 'flex', alignItems: 'center', gap: '5px', padding: '0 12px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff', boxSizing: 'border-box' }}>
                    <span>{getFilterLabel()}</span><span>▼</span>
                </button>
                {isFilterOpen && (
                    <div className="filter-dropdown-menu" style={{ top: '100%', marginTop: '4px' }}>
                        {STATUS_OPTIONS.filter(opt => viewMode === 'map' || opt.value !== 'archived').map(opt => (
                            <div key={opt.value} className="filter-option" onClick={() => toggleStatusFilter(opt.value)}>
                                <input type="checkbox" checked={statusFilter.includes(opt.value)} readOnly />
                                <span className="status-dot" style={{ backgroundColor: opt.color, marginRight: '5px' }}></span>
                                <span className="filter-label">{opt.label}</span>
                            </div>
                        ))}
                        <div className="filter-actions-row">
                            <small className="filter-action-btn action-all" onClick={(e) => { e.stopPropagation(); handleSelectAllFilters(); }}>Alle</small>
                            <span style={{ margin: '0 5px', color: '#ccc' }}>|</span>
                            <small className="filter-action-btn action-none" onClick={(e) => { e.stopPropagation(); handleSelectNoneFilters(); }}>Keine</small>
                        </div>
                    </div>
                )}
            </div>

            <div className="view-toggle" style={{
                display: 'flex',
                background: 'rgba(0,0,0,0.05)',
                padding: '4px',
                borderRadius: '10px',
                height: '40px',
                alignItems: 'center'
            }}>
                <button
                    onClick={() => setViewMode('list')}
                    style={{
                        padding: '0 16px',
                        height: '32px',
                        fontSize: '0.85rem',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: viewMode === 'list' ? 'white' : 'transparent',
                        boxShadow: viewMode === 'list' ? '0 2px 6px rgba(0,0,0,0.1)' : 'none',
                        color: viewMode === 'list' ? '#007aff' : '#86868b',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                    }}
                >
                    📋 Liste
                </button>
                <button
                    onClick={() => setViewMode('map')}
                    style={{
                        padding: '0 16px',
                        height: '32px',
                        fontSize: '0.85rem',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: viewMode === 'map' ? 'white' : 'transparent',
                        boxShadow: viewMode === 'map' ? '0 2px 6px rgba(0,0,0,0.1)' : 'none',
                        color: viewMode === 'map' ? '#007aff' : '#86868b',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                    }}
                >
                    🗺️ Karte
                </button>
            </div>

            {globalPermissions.create_projects && (
                <button onClick={openCreateProject} className="btn-primary" style={{ height: '40px', display: 'flex', alignItems: 'center', gap: '5px', padding: '0 16px', borderRadius: '8px' }}>
                    <span style={{ fontSize: '1.2rem', marginBottom: '2px' }}>+</span> Neues Projekt
                </button>
            )}

            {/* Trash Toggle (Far Right) */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button 
                    onClick={() => setIsTrashOpen(!isTrashOpen)}
                    style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: isTrashOpen ? 'rgba(255, 59, 48, 0.1)' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative'
                    }}
                    title="Papierkorb anzeigen"
                >
                    <span style={{ fontSize: '20px', filter: isTrashOpen ? 'none' : 'grayscale(100%)', opacity: isTrashOpen ? 1 : 0.6 }}>🗑️</span>
                    {trashCount > 0 && !isTrashOpen && (
                        <span style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            background: '#ff3b30',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #fff'
                        }}>
                            {trashCount}
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}
